import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Role-based Access Wrapper Component
 * Conditionally renders children based on user role
 *
 * @example
 * // Render only if user is a System Admin
 * <RequireRole roles={[ROLES.SYSTEM_ADMIN]}>
 *   <Button>Admin Only Button</Button>
 * </RequireRole>
 *
 * @example
 * // Render if user has ANY of the specified roles
 * <RequireRole roles={[ROLES.CENTER_ADMIN, ROLES.CUSTOMER_ADMIN]}>
 *   <Button>Admin Button</Button>
 * </RequireRole>
 *
 * @example
 * // Render if user has at least the minimum role level
 * <RequireRole minimumRole={ROLES.CENTER_DIRECTOR}>
 *   <Button>Staff Only</Button>
 * </RequireRole>
 *
 * @example
 * // Render fallback if no access
 * <RequireRole roles={[ROLES.SYSTEM_ADMIN]} fallback={<Text>Admin only</Text>}>
 *   <Button>Manage System</Button>
 * </RequireRole>
 *
 * @example
 * // Use feature-based access (from ROLE_FEATURES)
 * <RequireRole feature={{ page: 'LEAD_PAGE', name: 'CREATE_LEAD' }}>
 *   <Button>Create Lead</Button>
 * </RequireRole>
 */
const RequireRole = ({
  roles,
  minimumRole,
  feature,
  children,
  fallback = null
}) => {
  const { hasAnyRole, hasMinimumRole, canUseFeatureByRole } = usePermissions();

  // Feature-based access
  if (feature) {
    const hasAccess = canUseFeatureByRole(feature.page, feature.name);
    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // Minimum role level check
  if (minimumRole) {
    const hasAccess = hasMinimumRole(minimumRole);
    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // Specific roles check
  if (roles && roles.length > 0) {
    const hasAccess = hasAnyRole(roles);
    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // If no criteria specified, render children by default
  return <>{children}</>;
};

export default RequireRole;
